import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ArtButton, ArtChip, confirmDialog, useApiService } from "@art/shared-ui";
import {
    faDiagramProject,
    faEdit,
    faEnvelope,
    faEnvelopeOpenText,
    faLayerGroup,
    faPaperPlane,
    faPlus,
    faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { usePageWrapper } from "../../../hooks";
import { RootState } from "../../../store";
import FaIcon from "../../../components/utils/fa_icon";
import styles from "./email-config.module.scss";

type EmailConfigStatus = "pending" | "verified" | "failing" | "disabled";

// Mirrors GetEmailConfigurationResponse, backed by the
// tenant_email_configuration collection.
interface EmailConfig {
    id: string;
    tenant_name: string;
    project_key: string;
    project_name: string;
    environment_name?: string;
    provider: "smtp" | "google";
    smtp_host: string;
    smtp_port: number;
    smtp_encryption: string;
    smtp_username: string;
    smtp_password: string;
    from_name: string;
    from_email: string;
    status: EmailConfigStatus;
    last_error?: string;
    last_verified_at?: string;
    created_at?: string;
    updated_at?: string;
}

const statusChip: Record<EmailConfigStatus, { label: string; color: "success" | "warning" | "error" | "default" }> = {
    verified: { label: "Verified", color: "success" },
    pending: { label: "Pending Verification", color: "warning" },
    failing: { label: "Failing", color: "error" },
    disabled: { label: "Disabled", color: "default" },
};

const formatDate = (iso?: string) =>
    iso
        ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
        : "—";

export function EmailConfigListContent() {
    const apiService = useApiService();
    const navigate = useNavigate();
    const appState = useSelector((state: RootState) => state.appstate);
    const projectKey: string = appState?.project_key || "";
    const projectName: string = appState?.project_name || projectKey;
    const environment: string = appState?.environment || "";

    // One email sender per (project, environment) — the sender identity usually
    // differs between dev/staging/prod, so both must be selected.
    const scopeReady = !!projectKey && !!environment;

    const [loading, setLoading] = useState(true);
    const [config, setConfig] = useState<EmailConfig | null>(null);
    const [testing, setTesting] = useState(false);

    const fetchConfig = useCallback(async () => {
        if (!scopeReady) {
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const res = await apiService.get("/notification/email-configuration", {
                showErrorSnack: false,
            });
            const list: EmailConfig[] = res.data?.data?.email_configurations ?? [];
            const scoped =
                list.find(
                    (c) =>
                        c.project_key === projectKey &&
                        (!c.environment_name || c.environment_name === environment)
                ) ?? null;
            setConfig(scoped);
        } catch (e) {
            setConfig(null);
        } finally {
            setLoading(false);
        }
    }, [apiService, scopeReady, projectKey, environment]);

    useEffect(() => {
        fetchConfig();
    }, [fetchConfig]);

    const configured = !!config;
    const chip = config ? statusChip[config.status] ?? { label: config.status, color: "default" as const } : null;

    const sendTestEmail = async () => {
        if (!config || testing) return;
        setTesting(true);
        try {
            const response = await apiService.post(`/notification/email-configuration/${config.id}/test`, {});
            const sentTo = response?.data?.data?.sent_to;
            apiService.showSnack(`Test email sent${sentTo ? ` to ${sentTo}` : ""} — check your inbox`, "success");
        } catch (error: any) {
            apiService.showSnack(
                error?.response?.data?.message || "Test failed — check the configuration",
                "error"
            );
        } finally {
            setTesting(false);
            fetchConfig();
        }
    };

    const editConfig = () => {
        if (!config) {
            navigate("/application-settings/email-config/new");
            return;
        }
        if (config.provider === "google") {
            apiService.showSnack("Google connections can't be edited — delete and reconnect instead", "info");
            return;
        }
        navigate(`/application-settings/email-config/${config.id}`);
    };

    const openTemplates = () =>
        navigate(`/application-settings/email-config/${config?.project_key || projectKey}/templates`, {
            state: { projectName: config?.project_name || projectName },
        });

    const removeConfig = () => {
        if (!config) return;
        confirmDialog(
            "confirm",
            `Remove the email configuration for ${projectName} / ${environment}? Emails for this project will fall back to the platform default sender.`
        ).then(() => {
            apiService.deleteData(`/notification/email-configuration/${config.id}`, {
                onSuccess: () => {
                    apiService.showSnack("Email configuration deleted", "success");
                    fetchConfig();
                },
                onError: (err: any) => {
                    apiService.showSnack(
                        err?.response?.data?.message || "Failed to delete email configuration",
                        "error"
                    );
                },
            })();
        });
    };

    return (
        <div className={styles.page}>
            <div className={styles.toolbar}>
                {projectKey && (
                    <button
                        type="button"
                        className={styles.badge}
                        data-qa="email-project-badge"
                        title="Switch project (App Settings)"
                        onClick={() => navigate("/application-settings/general")}
                    >
                        <FaIcon icon={faDiagramProject} />
                        {projectName}
                    </button>
                )}
                {environment && (
                    <button
                        type="button"
                        className={styles.badge}
                        data-qa="email-env-badge"
                        title="Switch environment (App Settings)"
                        onClick={() => navigate("/application-settings/general")}
                    >
                        <FaIcon icon={faLayerGroup} />
                        {environment}
                    </button>
                )}
                <span className={styles.scope_hint}>
                    The email sender is scoped to the selected project &amp; environment.
                </span>
            </div>

            {!scopeReady ? (
                <div className={styles.empty}>
                    <FaIcon icon={faDiagramProject} className={styles.empty_icon} />
                    <span className={styles.empty_title}>Select a project and environment</span>
                    <span className={styles.empty_sub}>
                        Each project &amp; environment has its own email sender (dev/staging/prod usually
                        send from different addresses). Select both to continue.
                    </span>
                    <ArtButton
                        variant="outlined"
                        dataQa="btn-select-scope"
                        label="Go to App Settings"
                        onClick={() => navigate("/application-settings/general")}
                    />
                </div>
            ) : loading ? (
                <div className={styles.loading}>Loading email configuration…</div>
            ) : !configured ? (
                <div className={styles.empty}>
                    <FaIcon icon={faEnvelope} className={styles.empty_icon} />
                    <span className={styles.empty_title}>No email sender configured</span>
                    <span className={styles.empty_sub}>
                        Add an SMTP server or connect a Google account so platform emails (invites, alerts,
                        invoices, etc.) are sent from your own address instead of the platform default.
                    </span>
                    <ArtButton
                        variant="contained"
                        dataQa="btn-add-email-config"
                        label="Add Configuration"
                        icon={faPlus}
                        onClick={() => navigate("/application-settings/email-config/new")}
                    />
                </div>
            ) : (
                <div className={styles.workspace}>
                    <section className={styles.sender_card}>
                        <div className={styles.sender_summary}>
                            <div>
                                <div className={styles.sender_title_row}>
                                    <h2 className={styles.sender_title}>{config!.from_name}</h2>
                                    {chip && (
                                        <ArtChip
                                            label={testing ? "Testing…" : chip.label}
                                            color={chip.color}
                                            size="small"
                                        />
                                    )}
                                </div>
                                <dl className={styles.sender_facts}>
                                    <div>
                                        <dt>From email</dt>
                                        <dd>{config!.from_email}</dd>
                                    </div>
                                    <div>
                                        <dt>Environment</dt>
                                        <dd>{config!.environment_name || environment}</dd>
                                    </div>
                                    <div>
                                        <dt>Provider</dt>
                                        <dd>{config!.provider === "google" ? "Google Workspace" : "SMTP"}</dd>
                                    </div>
                                    {config!.provider !== "google" && (
                                        <div>
                                            <dt>SMTP server</dt>
                                            <dd>
                                                {config!.smtp_host}:{config!.smtp_port} (
                                                {config!.smtp_encryption?.toUpperCase() || "—"})
                                            </dd>
                                        </div>
                                    )}
                                    {config!.provider !== "google" && (
                                        <div>
                                            <dt>Username</dt>
                                            <dd>{config!.smtp_username || "—"}</dd>
                                        </div>
                                    )}
                                    <div>
                                        <dt>Last verified</dt>
                                        <dd>{formatDate(config!.last_verified_at)}</dd>
                                    </div>
                                </dl>
                            </div>
                            <div
                                className={`${styles.sender_mark} ${
                                    config!.provider === "google" ? styles.google : styles.smtp
                                }`}
                                aria-hidden="true"
                            >
                                <FaIcon icon={faEnvelope} />
                            </div>
                        </div>

                        <div className={styles.sender_actions_panel}>
                            <h3 className={styles.sender_panel_title}>Sender Configuration</h3>
                            <p className={styles.sender_panel_copy}>
                                {config!.status === "verified"
                                    ? "Your email sender is verified and ready to use. Platform emails for this scope are sent from your configured address."
                                    : config!.status === "failing"
                                    ? "The last delivery attempt failed. Review the error below and update the configuration."
                                    : config!.status === "disabled"
                                    ? "This sender is disabled — emails fall back to the platform default until it is re-enabled."
                                    : "Send a test email to verify this configuration before it is used for platform emails."}
                            </p>
                            <div className={styles.panel_actions}>
                                <ArtButton
                                    variant="outlined"
                                    icon={faEdit}
                                    dataQa="btn-edit-email-config"
                                    label="Edit Configuration"
                                    onClick={editConfig}
                                />
                                <ArtButton
                                    variant="outlined"
                                    icon={faPaperPlane}
                                    dataQa="btn-test-email-config"
                                    label={testing ? "Sending…" : "Send Test Email"}
                                    disabled={testing}
                                    onClick={sendTestEmail}
                                />
                                <ArtButton
                                    variant="outlined"
                                    icon={faEnvelopeOpenText}
                                    dataQa="btn-email-templates"
                                    label="Manage Templates"
                                    onClick={openTemplates}
                                />
                                <ArtButton
                                    variant="outlined"
                                    color="error"
                                    icon={faTrash}
                                    dataQa="btn-delete-email-config"
                                    label="Remove"
                                    onClick={removeConfig}
                                />
                            </div>
                        </div>

                        {config!.status === "failing" && config!.last_error && (
                            <div className={styles.error_banner}>{config!.last_error}</div>
                        )}
                    </section>
                </div>
            )}
        </div>
    );
}

export default usePageWrapper(EmailConfigListContent, {
    page_title: "Email Configuration",
});
